import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  FieldType,
  SelectChoiceField,
  Session,
  SessionQuota,
  useCountParticipationByStatus,
  useForm,
  useGroupedParticipationFieldAnswersCount,
  useGroupedParticipationsItemsCount,
  useParticipantFields,
  useQuotas,
  useRecruitment,
} from '@app/api';
import { AnswerChart } from './components/Stats/AnswerChart';
import { LabeledIconChip } from '@app/core';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@app/form';
import { useUnmoderatedTabs } from './hooks/UnmoderatedTabsConfig';
import { WithOverviewColumnLayout } from './components/WithOverviewColumnLayout';
import { MansoryCard, MansoryGrid } from './components/MansoryGrid';
import { RangeAnswerChart } from './components/Stats/RangeAnswerChart';
import { getQuestionNumber } from 'components/CustomForm/FormBuilder/lib/getQuestionNumber';
import { FieldNumber } from 'components/CustomForm/components/FieldNumber';

const useStyles = createUseStyles((theme: Theme) => ({
  chartWrapper: {
    marginTop: theme.marginBase * 4,
  },
  selectContainer: {
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 3,
    padding: theme.marginBase * 2,
    background: theme.colors.white,
    boxShadow: theme.boxShadow.base,
    position: 'relative',
    zIndex: 1,
    borderRadius: theme.borderRadius.std,
  },
  selectContent: {
    display: 'flex',
    alignItems: 'center',
  },
  textStatus: {
    ...theme.fonts.h5,
    color: theme.colors.inkSecondary,
    paddingLeft: theme.marginBase,
    paddingRight: theme.marginBase,
    whiteSpace: 'nowrap',
  },
  fieldContainer: {
    display: 'flex',
    gap: theme.marginBase,
    paddingTop: theme.marginBase * 2,
    alignItems: 'baseline',
  },
  numberContainer: {
    position: 'relative',
  },
  fieldTitle: {
    ...theme.fonts.h5,
    paddingBottom: 0,
  },
}));

interface Props {
  session: Session;
  canSeeQuotas?: boolean;
}

export const RecruitmentMonitoring = ({ session, canSeeQuotas }: Props) => {
  const classes = useStyles();
  const tabs = useUnmoderatedTabs(canSeeQuotas);

  const availableStatuses = Object.keys(tabs).filter(
    (tab) => tabs[tab].visibleOnGraph,
  );

  const { data } = useGroupedParticipationFieldAnswersCount(session.id);
  const { data: itemsCount } = useGroupedParticipationsItemsCount(session.id);
  const { data: countParticipationStatus } = useCountParticipationByStatus(
    session.id,
  );
  const { data: participantFields } = useParticipantFields();
  const { data: recruitment } = useRecruitment(session.recruitmentId);

  const { data: form } = useForm(recruitment?.screeningFormId);
  const { data: quotas } = useQuotas(session.id, null, true);

  const getCountByStatus = (status: string): number => {
    return tabs?.[status].statusList.reduce((acc, status) => {
      return acc + (countParticipationStatus?.[status] || 0);
    }, 0);
  };

  const getFieldQuotas = (field: SelectChoiceField) => {
    if (field.type === FieldType.SINGLE_CHOICE) {
      return recruitment?.criteria.find((c) => c.fieldRef === field.ref)
        ?.quotas;
    } else if (field.type === FieldType.MULTIPLE_CHOICE) {
      return quotas?.filter((q) => q.ref === field.ref);
    }

    return undefined;
  };

  const fields = useMemo(() => {
    if (!form || !form.fields) {
      return [];
    }

    return form.fields.filter(
      (field) =>
        field.type === FieldType.SINGLE_CHOICE ||
        field.type === FieldType.MULTIPLE_CHOICE,
    ) as SelectChoiceField[];
  }, [form]);

  const [statusActive, setStatusActive] = useState<string[]>(
    availableStatuses.filter(
      (status) => !['rejected', 'outOfQuota'].includes(status),
    ),
  );

  const devices = useMemo(() => {
    return session.allowedDevicesCategories || [];
  }, [session]);

  const { t } = useTranslation();
  return (
    <WithOverviewColumnLayout session={session}>
      <div className={classes.selectContainer}>
        {availableStatuses.map((status) => (
          <div key={status} className={classes.selectContent}>
            <Checkbox
              checked={statusActive.includes(status)}
              onChange={(e) =>
                setStatusActive(
                  e.target.checked
                    ? [...statusActive, status]
                    : statusActive.filter((s) => s !== status),
                )
              }
              noMargin
            />
            <p className={classes.textStatus}>{tabs[status].label}</p>
            <LabeledIconChip
              chipLabel={getCountByStatus(status).toString()}
              labelColor={tabs[status].color}
            />
          </div>
        ))}
      </div>
      <MansoryGrid className={classes.chartWrapper}>
        {participantFields?.map((field) => (
          <>
            {data?.[field.name] && field.choices && (
              <MansoryCard>
                <AnswerChart
                  statusList={statusActive}
                  key={field.name}
                  title={field.label}
                  choices={field.choices}
                  data={data?.[field.name]}
                  quotas={
                    recruitment?.criteria.find((c) => c.fieldRef === field.name)
                      ?.quotas
                  }
                />
              </MansoryCard>
            )}
            {data?.[field.name] && !field.choices && field.range?.step && (
              <MansoryCard>
                <RangeAnswerChart
                  statusList={statusActive}
                  key={field.name}
                  title={field.range.label || field.label}
                  range={field.range}
                  data={data?.[field.name]}
                  criterion={recruitment?.criteria.find(
                    (c) => c.fieldRef === field.name,
                  )}
                />
              </MansoryCard>
            )}
          </>
        ))}
        {fields.map(
          (field) =>
            field.properties.choices && (
              <MansoryCard>
                <AnswerChart
                  statusList={statusActive}
                  key={field.id}
                  title={
                    <div className={classes.fieldContainer}>
                      <div className={classes.numberContainer}>
                        {
                          <FieldNumber
                            number={getQuestionNumber(fields, field)}
                          />
                        }
                      </div>
                      <span className={classes.fieldTitle}>{field.title}</span>
                    </div>
                  }
                  choices={field.properties.choices}
                  data={data?.[field.ref]}
                  quotas={getFieldQuotas(field)}
                />
              </MansoryCard>
            ),
        )}
        {devices.length > 1 && (
          <MansoryCard>
            <AnswerChart
              statusList={statusActive}
              key="devices"
              title={t('sessions.display.allowedDevicesLabel')}
              choices={devices.map((device) => ({
                label: t(
                  `sessions.display.allowedDevicesCategoriesChoices.${device}`,
                ),
                value: device,
              }))}
              data={itemsCount?.['ALLOWED_DEVICES_CATEGORY']}
              quotas={quotas?.filter(
                (q) => q.ref === SessionQuota.ALLOWED_DEVICES_CATEGORY,
              )}
            />
          </MansoryCard>
        )}
      </MansoryGrid>
    </WithOverviewColumnLayout>
  );
};
